import { DowngradeReason, PaymentRecord, PlanCode } from '@/api/planApi';

/**
 * 다운그레이드 불가 사유에 따른 메시지 반환
 */
export function getDowngradeMessage(reason: DowngradeReason): string {
  switch (reason) {
    case 'FREE_PLAN':
      return 'FREE 플랜은 최하위 플랜이므로 다운그레이드가 불가능합니다.';
    case 'STARTER_PLAN':
      return 'STARTER 플랜은 최하위 플랜이므로 다운그레이드가 불가능합니다.';
    case 'MONTHLY_LIMIT_EXCEEDED':
      return '이번 달 다운그레이드 횟수가 1회 사용되어 다운그레이드가 불가능합니다. 월 1회 다운그레이드 제한이 적용됩니다.';
    case 'PENDING_UPGRADE':
      return '업그레이드가 진행 중입니다. 업그레이드 완료 후 다운그레이드가 가능합니다.';
    case 'NOT_OWNER':
      return '팀원은 플랜 변경이 불가능합니다. 팀장에게 문의하세요.';
    default:
      return '다운그레이드가 불가능합니다.';
  }
}

/** 같은 계약/같은 팀(또는 개인)을 대표하는 결제 건들의 그룹 키 */
function paymentGroupKey(payment: PaymentRecord): string {
  return payment.contractId ? `contract-${payment.contractId}` : payment.teamId ? `team-${payment.teamId}` : 'personal';
}

/**
 * 대체된(superseded) 결제 건인지 확인 - 같은 그룹 내에 더 최근에 확정된(CONFIRMED/DONE)
 * 결제가 있거나, 더 최근 결제가 이미 환불 처리된 경우.
 *
 * 후자(더 최근 결제가 이미 환불된 경우)를 포함하는 이유: 최신 결제를 환불하면 status가
 * CANCELLED로 바뀌어 더 이상 "최신 CONFIRMED/DONE"로 안 잡히는데, 그러면 그 이전의
 * (원래는 대체됐던) 결제가 다시 "최신"으로 보여서 환불/청구서 버튼이 재노출되는 버그가
 * 있었음(2026-07-06 member02 엔터프라이즈 건에서 실제 발견) - 백엔드도 동일한 케이스를
 * 별도로 차단하지만, 화면에서도 미리 숨겨야 클릭 후 에러로 막히는 혼란스러운 경험을 피할 수 있다.
 */
export function isPaymentSuperseded(payment: PaymentRecord, allRecords: PaymentRecord[]): boolean {
  if (payment.status !== 'CONFIRMED' && payment.status !== 'DONE') return false;
  const group = paymentGroupKey(payment);
  const ownTime = new Date(payment.confirmedAt ?? payment.createdAt).getTime();
  return allRecords.some((other) => {
    if (other.id === payment.id) return false;
    if (paymentGroupKey(other) !== group) return false;
    const otherTime = new Date(other.confirmedAt ?? other.createdAt).getTime();
    if (otherTime <= ownTime) return false;
    if (other.status === 'CONFIRMED' || other.status === 'DONE') return true;
    if (other.status === 'CANCELLED' && !!other.refundAmount) return true;
    return false;
  });
}

/**
 * PENDING 결제가 실제 플랜 변경(업그레이드)이 아니라 이미 사용 중인 플랜에 대한
 * 미납 요금 청구(예: 관리자 소급청구 - TeamService.createRetroactiveTeamBilling)인지 판별한다.
 * 대상 plan이 현재 플랜과 같고 다운그레이드 표시도 아니면 플랜이 바뀐 적이 없으므로
 * "업그레이드 입금대기"로 라벨링하면 실제로는 아무것도 안 바뀌는데 바뀌는 것처럼 오해를 준다.
 */
export function isBackBillPending(pendingPayment: PaymentRecord | null, currentPlan: PlanCode): boolean {
  return !!pendingPayment && !pendingPayment.isDowngrade && pendingPayment.plan === currentPlan;
}
